import React, { ChangeEvent, forwardRef, ForwardedRef, useState } from "react"
import { StyledTextField } from "components/inputs/StyledTextField.tsx"
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined"
import { Box } from "@mui/system"

export interface FormattedTextFieldProps {
    label: string
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void
}

const FormattedBankNumberTextField: React.ForwardRefRenderFunction<
    HTMLInputElement,
    FormattedTextFieldProps
> = ({ label, onChange, ...rest }, ref: ForwardedRef<HTMLInputElement>) => {
    const [isFilled, setIsFilled] = useState(false)

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const inputValue = event.target.value.replace(/\D/g, "") // Supprime tous les caractères non numériques
        const formattedValue = inputValue.replace(/(.{4})/g, "$1 ") // Ajoute un espace tous les quatre caractères

        event.target.value = formattedValue.trim()
        if(onChange) onChange(event)
        setIsFilled(inputValue.length === 16)
    }

    return (
        <Box sx={{ position: "relative", color: "secondary.main" }}>
            <StyledTextField
                label={label}
                onChange={handleChange}
                bgcolor={"grey.100"}
                slotProps={{
                    input: {
                        inputProps: {
                            maxLength: 19,
                        },
                    },
                }}
                {...rest}
                ref={ref}
            />
            {isFilled && (
                <CheckCircleOutlineOutlinedIcon
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                />
            )}
        </Box>
    )
}

const FormattedExpirationDateTextField: React.ForwardRefRenderFunction<
    HTMLInputElement,
    FormattedTextFieldProps
> = ({ label, onChange, ...rest }, ref: ForwardedRef<HTMLInputElement>) => {
    const [isFilled, setIsFilled] = useState(false)
    const [verifyMonth, setVerifyMonth] = useState(true)
    const [verifyYear, setVerifyYear] = useState(true)

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const inputValue = event.target.value.replace(/\D/g, "")
        let formattedValue = ""

        if (inputValue.length >= 2) {
            formattedValue = `${inputValue.slice(0, 2)}`
            if (inputValue.length > 2) {
                formattedValue += `/${inputValue.slice(2, 6)}`
            }
        } else {
            formattedValue = inputValue
        }

        event.target.value = formattedValue.trim()
        if(onChange) onChange(event)
        setIsFilled(formattedValue.length === 5)

        if (formattedValue.length === 2) {
            event.target.value += "/"
        }

        if (formattedValue.length === 5) {
            const month = parseInt(inputValue.slice(0, 2), 10)
            const year = parseInt(formattedValue.slice(3), 10)
            const currentYear = new Date().getFullYear() % 100 // Obtient les 2 derniers chiffres de l'année actuelle

            setVerifyMonth(month >= 1 && month <= 12)
            setVerifyYear(year >= currentYear)
        }
    }
    return (
        <Box sx={{ position: "relative", color: "secondary.main" }}>
            <StyledTextField
                label={label}
                onChange={handleChange}
                bgcolor={"grey.100"}
                error={!verifyMonth || !verifyYear}
                helperText={
                    !verifyMonth
                        ? "Mois invalide"
                        : !verifyYear
                          ? "Année invalide"
                          : null
                }
                slotProps={{
                    input: {
                        inputProps: {
                            maxLength: 5,
                        },
                    },
                }}
                {...rest}
                ref={ref}
            />

            {verifyYear && verifyMonth && isFilled && (
                <CheckCircleOutlineOutlinedIcon
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                />
            )}
        </Box>
    )
}

const FormattedCVVTextField: React.ForwardRefRenderFunction<
    HTMLInputElement,
    FormattedTextFieldProps
> = ({ label, onChange, ...rest }, ref: ForwardedRef<HTMLInputElement>) => {
    const [isFilled, setIsFilled] = useState(false)
    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const inputValue = event.target.value.replace(/\D/g, "")
        event.target.value = inputValue.trim()
        if(onChange) onChange(event)
        setIsFilled(inputValue.length === 3)
    }

    return (
        <Box sx={{ position: "relative", color: "secondary.main" }}>
            <StyledTextField
                label={label}
                onChange={handleChange}
                bgcolor={"grey.100"}
                slotProps={{
                    input: {
                        inputProps: {
                            maxLength: 3,
                        },
                    },
                }}
                {...rest}
                ref={ref}
            />
            {isFilled && (
                <CheckCircleOutlineOutlinedIcon
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                />
            )}
        </Box>
    )
}

const ForwardedFormattedBankNumberTextField = forwardRef(
    FormattedBankNumberTextField
)
const ForwardedFormattedExpirationDateTextField = forwardRef(
    FormattedExpirationDateTextField
)
const ForwardedFormattedCVVTextField = forwardRef(FormattedCVVTextField)

export {
    ForwardedFormattedBankNumberTextField,
    ForwardedFormattedExpirationDateTextField,
    ForwardedFormattedCVVTextField,
}
